import type { ChartType, SecondaryDimensionValues } from '@revelio/core';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { ChartFallback } from '../../Chart/ChartFallback';
import type { ResolvedData } from '../../DataResolver/resolve-items';
import { useCompositionData } from '../composition/useCompositionData';
import { usePostingsData } from '../postings/usePostingsData';
import type {
  DataProviderChildren,
  SingleDataSet,
  TimeseriesDataSet,
} from '../types';
import { combineQueryStatuses } from '../utils/combine-query-statuses';
import {
  computeMarketTightnessDataSet,
  computeMarketTightnessFromTable,
} from './compute-market-tightness';

type MarketTightnessData = SingleDataSet | TimeseriesDataSet | undefined;

export const MarketTightnessDataProvider = ({
  primaryDimension,
  secondaryDimension,
  chartType,
  composition,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension?: SecondaryDimensionValues;
  chartType: ChartType;
  isTimeseries?: boolean;
  composition?: ResolvedData;
  children: DataProviderChildren<MarketTightnessData>;
}) => {
  const isMarketTightnessTimeseries =
    chartType === 'line' ||
    chartType === 'volume' ||
    secondaryDimension === 'timeseries';

  const {
    compositionData,
    monthRefs,
    status: compositionStatus,
  } = useCompositionData(primaryDimension, {
    pause: composition !== undefined,
  });
  const {
    postingsData,
    weekRefs,
    status: postingsStatus,
  } = usePostingsData(primaryDimension);
  const status = combineQueryStatuses([
    composition?.status ?? compositionStatus,
    postingsStatus,
  ]);

  const data: MarketTightnessData = (() => {
    if (!postingsData) return undefined;
    if (composition) {
      return (
        composition.data &&
        computeMarketTightnessFromTable({
          table: composition.data,
          postingData: postingsData.entities,
          weekRefs,
          isTimeseries: isMarketTightnessTimeseries,
        })
      );
    }
    return compositionData
      ? computeMarketTightnessDataSet({
          compositionData: compositionData.entities,
          postingData: postingsData.entities,
          monthRefs,
          weekRefs,
          isTimeseries: isMarketTightnessTimeseries,
        })
      : undefined;
  })();

  return (
    <ChartFallback status={status}>{children({ data, status })}</ChartFallback>
  );
};
